import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { withRouter } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { message } from "antd";
import { InlineWidget } from "react-calendly";
import AuthenticationApiSerivce from "../../../../api/AuthServices";
import { RegistrationLayout } from "../../../RegistrationLayout/RegistrationLayout";
import { RegistrationSetupNavbar } from "../../../RegistrationLayout/RegistrationSetupNavbar";
import {
  REGISTRATION_STEP_TITLE_PROPS,
  useRegistrationStepFocus,
} from "../../../../hook/useRegistrationStepFocus";
import { useAnnounceWhen } from "../../../../hook/useA11yAnnouncements";
import RegistrationSetupWrapper from "../../RegistrationSetup.style";
import {
  REGISTRATION_SETUP_STEP_META,
  REGISTRATION_SETUP_TOTAL_STEPS,
} from "../../../../helper/constants";
import {
  getRegistrationData,
  updateRegistrationData,
  isCardOnFileComplete,
} from "../../registrationSetupStorage";
import { getStoredPaymentTypeFields } from "../../registrationPaymentType";
import { registrationGoBack } from "../../registrationNavigation";
import {
  buildCalendlyBookingUrl,
  buildCompleteBookingPayload,
  CALENDLY_EVENT_SCHEDULED,
  CALENDLY_ORIGIN,
  getAppointmentConfirmationNumber,
  getRegistrationUserEmail,
  getRegistrationUserName,
  isCompleteBookingApiSuccess,
  isSessionExpiredError,
  resolveRegistrationRdId,
} from "../calendlyBooking";
import BookingConfirmationRetryModal from "./BookingConfirmationRetryModal";
import {
  trackBookingCompleted,
  trackBookingStarted,
  trackRegistrationBookingConfirmed,
  trackRegistrationError,
} from "../../../../helper/registrationAnalytics";

const APPOINTMENT_STEP = REGISTRATION_SETUP_STEP_META.APPOINTMENT_TIME;

const resolveBookingErrorMessage = (err, fallback, translate) => {
  const errorMessage = err?.message;
  if (!errorMessage || errorMessage === "complete-booking-failed") {
    return fallback;
  }
  if (errorMessage.startsWith("NUTRITION_")) {
    return translate(errorMessage);
  }
  return errorMessage;
};

const RegistrationCalendlyBooking = ({ history }) => {
  const { t, i18n } = useTranslation();
  const stepTitleRef = useRegistrationStepFocus();
  const hasConfirmedRef = useRef(false);
  const isBookingInFlightRef = useRef(false);
  const pendingCalendlyPayloadRef = useRef(null);
  const [isCompletingBooking, setIsCompletingBooking] = useState(false);
  const [bookingRetryModal, setBookingRetryModal] = useState({
    isOpen: false,
    message: "",
  });
  const confirmingMessage = t("NUTRITION_CALENDAR_LBL_CONFIRMING", {
    defaultValue: "Confirming your appointment...",
  });
  useAnnounceWhen(isCompletingBooking, confirmingMessage, "important");

  const registrationData = useMemo(() => getRegistrationData(), []);
  const selectedSlot = registrationData.selectedAppointment;
  const selectedDietitian = registrationData.selectedDietitian;
  const userName = getRegistrationUserName(registrationData);
  const userEmail = getRegistrationUserEmail(registrationData);

  const calendlyUrl = useMemo(
    () =>
      buildCalendlyBookingUrl(
        selectedSlot?.schedulingUrl,
        userName,
        userEmail
      ),
    [selectedSlot?.schedulingUrl, userName, userEmail]
  );

  useEffect(() => {
    if (calendlyUrl) {
      trackBookingStarted();
    }
  }, [calendlyUrl]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById("root");
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevRootHeight = root?.style.height ?? "";

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    if (root) {
      root.style.height = "100vh";
      root.style.height = "100dvh";
    }

    const data = getRegistrationData();
    if (!isCardOnFileComplete(data)) {
      history.replace("/register/account/card");
      return () => {
        html.style.overflow = prevHtmlOverflow;
        body.style.overflow = prevBodyOverflow;
        if (root) root.style.height = prevRootHeight;
      };
    }
    if (!data.selectedDietitian) {
      history.replace("/register/account/dietitians");
      return () => {
        html.style.overflow = prevHtmlOverflow;
        body.style.overflow = prevBodyOverflow;
        if (root) root.style.height = prevRootHeight;
      };
    }
    if (!data.selectedAppointment?.schedulingUrl) {
      const appointmentsRoute = data.noPreferenceSelectedSlot
        ? "/register/account/no-preference/appointments"
        : "/register/account/appointments";
      history.replace(appointmentsRoute);
    }

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      if (root) {
        root.style.height = prevRootHeight;
      }
    };
  }, [history]);

  const completeBooking = useCallback(
    async (calendlyEventPayload) => {
      if (calendlyEventPayload) {
        pendingCalendlyPayloadRef.current = calendlyEventPayload;
      }

      const calendlyPayload = pendingCalendlyPayloadRef.current;
      if (!calendlyPayload || isBookingInFlightRef.current) {
        return;
      }

      const rdId = resolveRegistrationRdId(selectedDietitian);
      if (!rdId) {
        message.error(t("NUTRITION_RDPROFILE_ERR_UNAVAILABLE"));
        hasConfirmedRef.current = false;
        return;
      }

      isBookingInFlightRef.current = true;
      setIsCompletingBooking(true);
      setBookingRetryModal({ isOpen: false, message: "" });

      try {
        const bookingPayload = buildCompleteBookingPayload({
          registrationData: getRegistrationData(),
          rdId,
          calendlyEventPayload: calendlyPayload,
        });

        const result = await AuthenticationApiSerivce.completeRegistrationBooking(
          bookingPayload,
          i18n.language
        );

        if (!isCompleteBookingApiSuccess(result)) {
          throw new Error(
            result?.message || "complete-booking-failed"
          );
        }

        const appointment = result.data ?? bookingPayload;
        const confirmationNumber = getAppointmentConfirmationNumber(appointment);

        updateRegistrationData({
          bookedAppointment: true,
          calendlyAppointment: appointment,
          ...(confirmationNumber ? { confirmationNumber } : {}),
        });

        const latestRegistrationData = getRegistrationData();
        const { paymentType } = getStoredPaymentTypeFields(latestRegistrationData);
        const providerId = resolveRegistrationRdId(selectedDietitian);

        trackRegistrationBookingConfirmed({
          paymentType,
          providerId,
        });
        trackBookingCompleted();
        history.push("/booking-confirmation", { appointment });
      } catch (err) {
        if (isSessionExpiredError(err)) {
          trackRegistrationError({
            stepName: "booking",
            stepNumber: 6,
            errorType: "unauthorized",
          });
          message.error(err.message);
          history.replace("/register/account");
          return;
        }

        trackRegistrationError({
          stepName: "booking",
          stepNumber: 6,
          errorType: "api_failed",
        });
        hasConfirmedRef.current = false;
        setBookingRetryModal({
          isOpen: true,
          message: resolveBookingErrorMessage(
            err,
            t("NUTRITION_COMMON_ERR_TRY_AGAIN"),
            t
          ),
        });
      } finally {
        isBookingInFlightRef.current = false;
        setIsCompletingBooking(false);
      }
    },
    [history, i18n.language, selectedDietitian, t]
  );

  const handleRetryBooking = () => {
    completeBooking();
  };

  useEffect(() => {
    const handleCalendlyEvent = (event) => {
      if (event.origin !== CALENDLY_ORIGIN) {
        return;
      }
      if (event.data?.event !== CALENDLY_EVENT_SCHEDULED) {
        return;
      }
      if (hasConfirmedRef.current) {
        return;
      }
      hasConfirmedRef.current = true;
      completeBooking(event.data.payload);
    };

    window.addEventListener("message", handleCalendlyEvent);
    return () => {
      window.removeEventListener("message", handleCalendlyEvent);
    };
  }, [completeBooking]);

  return (
    <RegistrationSetupWrapper>
      <RegistrationLayout
        containScroll
        header={
          <RegistrationSetupNavbar
            stepMeta={APPOINTMENT_STEP}
            totalSteps={REGISTRATION_SETUP_TOTAL_STEPS}
            onBack={() => registrationGoBack(history)}
            showBack={!isCompletingBooking}
          />
        }
      >
        <div className="scroll-body">
          <div className="content-card content-card-appointment">
            <div className="content-card-scroll">
              <h2
                className="section-title"
                ref={stepTitleRef}
                {...REGISTRATION_STEP_TITLE_PROPS}
              >
                {t("NUTRITION_CALENDAR_LBL_TITLE")}
              </h2>
              <p className="section-sub">
                {t("NUTRITION_CALENDAR_DESC_SUBTITLE")}
              </p>

              {calendlyUrl ? (
                <div className="calendly-widget-wrap">
                  <InlineWidget
                    url={calendlyUrl}
                    styles={{
                      minWidth: "320px",
                      height: "700px",
                    }}
                  />

                  {isCompletingBooking ? (
                    <div
                      className="calendly-booking-overlay calendly-booking-overlay--loading"
                      aria-busy="true"
                    >
                      <div className="discovery-spinner-wrap">
                        <div className="discovery-spinner" aria-hidden />
                      </div>
                      <p className="section-sub">{confirmingMessage}</p>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </RegistrationLayout>

      <BookingConfirmationRetryModal
        isOpen={bookingRetryModal.isOpen}
        message={bookingRetryModal.message}
        onRetry={handleRetryBooking}
        onClose={() => setBookingRetryModal({ isOpen: false, message: "" })}
        isRetrying={isCompletingBooking}
      />
    </RegistrationSetupWrapper>
  );
};

export default withRouter(RegistrationCalendlyBooking);
